import React from 'react'
import ChromeIcon from './IconsComponents/ChromeIcon'
import FirefoxIcon from './IconsComponents/FirefoxIcon'
import EdgeIcon from './IconsComponents/EdgeIcon'
import { platform } from '../constants'
import {
  Menu,
  OverflowMenuHorizontal,
  OverflowMenuVertical,
  SettingsAdjust,
  Video,
} from '@carbon/icons-react'
import { getImagePath } from '../utils/getBrowsersImages'
import { getPermissionImagePath } from '../utils/getBrowsersPermissionImages'
import { getSettingsImagePath } from '../utils/getBrowsersSettingsImages'
import { useTranslation, Trans } from 'react-i18next'
import { ReloadButton } from './ReloadButton'

interface BrowserSpecificContentProps {
  browser: string
  os: string
  brand: string
  token: string
  language: string
  startDate: string
  endDate: string
  meetingNumber: string
  password: string
}

const DesktopContent: React.FC<BrowserSpecificContentProps> = ({
  browser,
  os,
  brand,
  token,
  language,
  startDate,
  endDate,
  meetingNumber,
  password,
}) => {
  const resolvedImagePath = getImagePath(browser, os)
  const permissionImagePath = getPermissionImagePath(browser, os)
  const settingsImagePath = getSettingsImagePath(browser, os)
  const { t } = useTranslation()
  const browserContentMap: { [key: string]: JSX.Element } = {
    mac_chrome: (
      <>
        {platform === 'desktop' && (
          <>
            <div className="flex flex-col gap-2 md:border-2 md:border-blue-200 md:p-4 md:rounded-2xl w-[390px] md:w-[862px] h-[140px] md:mt-10 ">
              <div className="flex gap-2">
                <p className="flex gap-1">
                  <span className="font-bold">1.</span>
                </p>
                <Trans
                  i18nKey="zoom.instructions.mac.chrome.stepOneA"
                  components={{
                    icon: <SettingsAdjust className="mt-1" />,
                  }}
                />
              </div>
              <div className="flex items-center gap-1">
                <ChromeIcon />
                <Trans
                  i18nKey="zoom.instructions.mac.chrome.stepOneB"
                  components={{
                    browser: (
                      <span className="flex gap-2 ">
                        <p className="inline-block">
                          {t('zoom.instructions.mac.chrome.browser')}
                        </p>
                      </span>
                    ),
                  }}
                />
              </div>
              <img src={resolvedImagePath} alt={browser} className="mt-2" />
            </div>
            <div className="flex border-2 border-blue-200 p-4 rounded-2xl w-[862px] h-[172px]">
              <div className="w-[550px] mr-28">
                <p>
                  <span className="font-bold">2. </span>
                  <Trans
                    i18nKey="zoom.instructions.mac.chrome.stepTwoA"
                    components={{ strong: <span className="font-bold" /> }}
                  />
                </p>
              </div>
              <div className="flex">
                <img
                  src={permissionImagePath}
                  alt="Permissions screen"
                
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px] h-[140px]">
              <p className="w-[80%]">
                <span className="font-bold">3. </span>
                <Trans
                  i18nKey="zoom.instructions.mac.chrome.stepThree"
                  components={{
                    strong: <span className="font-bold" />,
                  }}
                />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.mac.chrome.reloadButton"
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                  token={token}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4 border-2 border-blue-200 p-4 rounded-2xl w-[862px] h-auto">
              <p>
                <span className="font-bold">4. </span>
                <Trans
                  i18nKey="zoom.instructions.mac.chrome.stepFour"
                  components={{
                    icon: <OverflowMenuVertical className=" inline-flex" />,
                    span: <span className="inline-flex items-center gap-1" />,
                    strong: <span className="font-bold" />,
                  }}
                />
              </p>
              <div className="flex gap-2 justify-center items-center">
                <img
                  src={settingsImagePath}
                  alt="Settings screen"
                  className="w-[755px] h-[56px]"
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px] h-[140px]">
              <p className="w-[80%]">
                <span className="font-bold">5. </span>
                <Trans
                  i18nKey="zoom.instructions.mac.chrome.stepFive"
                  components={{
                    strong: <span className="font-bold" />,
                  }}
                />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.mac.chrome.reloadButton"
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                  token={token}
                />
              </div>
            </div>
          </>
        )}
      </>
    ),
    mac_firefox: (
      <>
        {platform === 'desktop' && (
          <>
            <div className="md:border-2 md:border-blue-200 md:p-4 md:rounded-2xl w-[390px] md:w-[862px]  md:mt-10">
              <div className="flex flex-col gap-2">
                <p className="flex items-center gap-1">
                  <span className="font-bold">1. </span>
                  <Trans
                    i18nKey="zoom.instructions.mac.firefox.stepOne"
                    components={{
                      icon: <Video />,
                    }}
                  />
                </p>
                <div className="flex gap-2 items-center">
                  <FirefoxIcon />
                  <p>{t('zoom.instructions.mac.firefox.browser')}</p>
                </div>
                <img src={resolvedImagePath} alt={browser} className="mt-2" />
              </div>
            </div>
            <div className="flex border-2 border-blue-200 p-4 rounded-2xl w-[862px] ">
              <div className="w-[550px] mr-28">
                <p>
                  <span className="font-bold">2. </span>
                  <Trans i18nKey="zoom.instructions.mac.firefox.stepTwo" />
                </p>
              </div>
              <div className="flex justify-center items-center">
                <img
                  src={permissionImagePath}
                  alt="Permissions screen"
                  className="w-[228px] h-[75px]"
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px] ">
              <p className="w-[80%]">
                <span className="font-bold">3. </span>
                <Trans i18nKey="zoom.instructions.mac.firefox.stepThree" />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.mac.firefox.reloadButton"
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                  token={token}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4 border-2 border-blue-200 p-4 rounded-2xl w-[862px] h-auto">
              <p>
                <span className="font-bold">4. </span>
                <Trans
                  i18nKey="zoom.instructions.mac.firefox.stepFour"
                  components={{
                    icon: <Menu className=" inline-flex" />,
                    span: <span className="inline-flex items-center gap-1" />,
                    strong: <span className="font-bold" />,
                  }}
                />
              </p>
              <div className="flex gap-2 items-center">
                <img
                  src={settingsImagePath}
                  alt="Settings screen"
                  className="w-[330px] h-[67px]"
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px] h-[140px]">
              <p className="w-[80%]">
                <span className="font-bold">5. </span>
                <Trans i18nKey="zoom.instructions.mac.firefox.stepFive" />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.mac.firefox.reloadButton"
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                  token={token}
                />
              </div>
            </div>
          </>
        )}
      </>
    ),
    mac_safari: (
      <>
        {platform === 'desktop' && (
          <>
            <div className="md:border-2 md:border-blue-200 md:p-4 md:rounded-2xl w-[390px] md:w-[862px] h-[140px] md:mt-10">
              <div className="flex flex-col gap-2">
                <p className="w-[80%]">
                  <span className="font-bold">1. </span>
                  <Trans i18nKey="zoom.instructions.mac.safari.stepOne" />
                </p>
                <div className="flex justify-center items-center">
                  <ReloadButton
                    brand={brand}
                    translationKey="zoom.instructions.mac.safari.reloadButton"
                    startDate={startDate}
                    endDate={endDate}
                    language={language}
                    token={token}
                  />
                </div>
              </div>
            </div>
            <div className="flex border-2 border-blue-200 p-4 rounded-2xl w-[862px] h-[172px]">
              <div className="w-[550px] mr-28">
                <p>
                  <span className="font-bold">2. </span>
                  <Trans i18nKey="zoom.instructions.mac.safari.stepTwo" />
                </p>
              </div>
              <div className="flex">
                <img
                  src={permissionImagePath}
                  alt="Permissions screen"
                  className="w-[148px]"
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px]">
              <p className="w-[80%]">
                <span className="font-bold">3. </span>
                <Trans i18nKey="zoom.instructions.mac.safari.stepThree" />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.mac.safari.reloadButton"
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                  token={token}
                />
              </div>
            </div>
            <div className="flex justify-between gap-4 border-2 border-blue-200 p-4 rounded-2xl w-[862px]">
              <p className="w-[355px]">
                <span className="font-bold">4.</span>
                <Trans i18nKey="zoom.instructions.mac.safari.stepFour" />
              </p>
              <div className="flex gap-2 justify-center items-center">
                <img
                  src={settingsImagePath}
                  alt="Settings screen"
                  className="w-[250px]"
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px] ">
              <p className="w-[80%]">
                <span className="font-bold">5. </span>
                <Trans i18nKey="zoom.instructions.mac.safari.stepFive" />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.mac.safari.reloadButton"
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                  token={token}
                />
              </div>
            </div>
          </>
        )}
      </>
    ),
    mac_edge: (
      <>
        {platform === 'desktop' && (
          <>
            <div className="md:border-2 md:border-blue-200 md:p-4 md:rounded-2xl w-[390px] md:w-[862px] h-[140px] md:mt-10">
              <div className="flex flex-col gap-2">
                <p className="flex items-center gap-1">
                  <span className="font-bold">1. </span>
                  <Trans
                    i18nKey="zoom.instructions.mac.edge.stepOne"
                    components={{
                      icon: <Video />,
                    }}
                  />
                </p>
                <div className="flex gap-2 items-center">
                  <EdgeIcon />
                  <p>{t('zoom.instructions.mac.edge.browser')}</p>
                </div>
                <img src={resolvedImagePath} alt={browser} className="mt-2" />
              </div>
            </div>
            <div className="flex border-2 border-blue-200 p-4 rounded-2xl w-[862px] ">
              <div className="w-[550px] mr-28">
                <p>
                  <span className="font-bold">2. </span>
                  <Trans i18nKey="zoom.instructions.mac.edge.stepTwo" />
                </p>
              </div>
              <div className="flex">
                <img
                  src={permissionImagePath}
                  alt="Permissions screen"
                  className="w-[120px] h-[148px]"
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px]">
              <p className="w-[80%]">
                <span className="font-bold">3. </span>
                <Trans i18nKey="zoom.instructions.mac.edge.stepThree" />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.mac.edge.reloadButton"
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                  token={token}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4 border-2 border-blue-200 p-4 rounded-2xl w-[862px]">
              <p>
                <span className="font-bold">4. </span>
                <Trans
                  i18nKey="zoom.instructions.mac.edge.stepFour"
                  components={{
                    icon: <OverflowMenuHorizontal className=" inline-flex" />,
                    span: <span className="inline-flex items-center gap-1" />,
                    strong: <span className="font-bold" />,
                  }}
                />
              </p>
              <div className="flex gap-2 justify-center items-center">
                <img
                  src={settingsImagePath}
                  alt="Settings screen"
                  className="w-[755px] h-[56px]"
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px]">
              <p className="w-[80%]">
                <span className="font-bold">5. </span>
                <Trans i18nKey="zoom.instructions.mac.edge.stepFive" />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.mac.edge.reloadButton"
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                  token={token}
                />
              </div>
            </div>
          </>
        )}
      </>
    ),
    windows_chrome: (
      <>
        {platform === 'desktop' && (
          <>
            <div className="md:border-2 md:border-blue-200 md:p-4 md:rounded-2xl w-[390px] md:w-[862px] h-[140px] md:mt-10">
              <div className="flex flex-col gap-2">
                <p className="flex items-center gap-1">
                  <span className="font-bold">1. </span>
                  <Trans
                    i18nKey="zoom.instructions.windows.chrome.stepOne"
                    components={{
                      icon: <SettingsAdjust />,
                    }}
                  />
                </p>
                <div className="flex gap-2 items-center">
                  <ChromeIcon />
                  <p>{t('zoom.instructions.windows.chrome.browser')}</p>
                </div>
                <img
                  src={resolvedImagePath}
                  alt={browser}
                  className="mt-2 w-[300px]"
                />
              </div>
            </div>
            <div className="flex border-2 border-blue-200 p-4 rounded-2xl w-[862px]">
              <div className="w-[550px] mr-28">
                <p>
                  <span className="font-bold">2. </span>
                  <Trans i18nKey="zoom.instructions.windows.chrome.stepTwo" />
                </p>
              </div>
              <div className="flex">
                <img
                  src={permissionImagePath}
                  alt="Permissions screen"
                  
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px]">
              <p className="w-[80%]">
                <span className="font-bold">3. </span>
                <Trans i18nKey="zoom.instructions.windows.chrome.stepThree" />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.windows.chrome.reloadButton"
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                  token={token}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4 border-2 border-blue-200 p-4 rounded-2xl w-[862px]">
              <div className="flex gap-2 justify-center items-center">
                <img
                  src={settingsImagePath}
                  alt="Settings screen"
                  className="w-[755px] h-[56px]"
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px]">
              <p className="w-[80%]">
                <span className="font-bold">5. </span>
                <Trans i18nKey="zoom.instructions.windows.chrome.stepFive" />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.windows.chrome.reloadButton"
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                  token={token}
                />
              </div>
            </div>
          </>
        )}
      </>
    ),
    windows_edge: (
      <>
        {platform === 'desktop' && (
          <>
            <div className="md:border-2 md:border-blue-200 md:p-4 md:rounded-2xl w-[390px] md:w-[862px] h-[140px] md:mt-10">
              <div className="flex flex-col gap-2">
                <p className="flex items-center gap-1">
                  <span className="font-bold">1. </span>
                  <Trans
                    i18nKey="zoom.instructions.windows.edge.stepOne"
                    components={{
                      icon: <Video />,
                    }}
                  />
                </p>
                <div className="flex gap-2 items-center">
                  <EdgeIcon />
                  <p>{t('zoom.instructions.windows.edge.browser')}</p>
                </div>
                <img src={resolvedImagePath} alt={browser} className="mt-2" />
              </div>
            </div>
            <div className="flex border-2 border-blue-200 p-4 rounded-2xl w-[862px] h-[172px]">
              <div className="w-[550px] mr-28">
                <p>
                  <span className="font-bold">2. </span>
                  <Trans i18nKey="zoom.instructions.windows.edge.stepTwo" />
                </p>
              </div>
              <div className="flex">
                <img
                  src={permissionImagePath}
                  alt="Permissions screen"
                  className="w-[120px] h-[148px]"
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px] h-[140px]">
              <p className="w-[80%]">
                <span className="font-bold">3. </span>
                <Trans i18nKey="zoom.instructions.windows.edge.stepThree" />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.windows.edge.reloadButton"
                  token={token}
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4 border-2 border-blue-200 p-4 rounded-2xl w-[862px] h-auto">
              <p>
                <span className="font-bold">4. </span>
                <Trans
                  i18nKey="zoom.instructions.windows.edge.stepFour"
                  components={{
                    icon: <OverflowMenuHorizontal className=" inline-flex" />,
                    span: <span className="inline-flex items-center gap-1" />,
                    strong: <span className="font-bold" />,
                  }}
                />
              </p>
              <div className="flex gap-2 justify-center items-center">
                <img
                  src={settingsImagePath}
                  alt="Settings screen"
                  className="w-[300px] h-[107px]"
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px] h-[140px]">
              <p className="w-[80%]">
                <span className="font-bold">5. </span>
                <Trans i18nKey="zoom.instructions.windows.edge.stepFive" />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.windows.edge.reloadButton"
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                  token={token}
                />
              </div>
            </div>
          </>
        )}
      </>
    ),
    windows_firefox: (
      <>
        {platform === 'desktop' && (
          <>
            <div className="md:border-2 md:border-blue-200 md:p-4 md:rounded-2xl w-[390px] md:w-[862px] h-[140px] md:mt-10">
              <div className="flex flex-col gap-2">
                <p className="flex items-center gap-1">
                  <span className="font-bold">1. </span>
                  <Trans
                    i18nKey="zoom.instructions.windows.firefox.stepOne"
                    components={{
                      icon: <Video />,
                    }}
                  />
                </p>
                <div className="flex gap-2 items-center">
                  <FirefoxIcon />
                  <p>{t('zoom.instructions.windows.firefox.browser')}</p>
                </div>
                <img src={resolvedImagePath} alt={browser} className="mt-2" />
              </div>
            </div>
            <div className="flex border-2 border-blue-200 p-4 rounded-2xl w-[862px]">
              <div className="w-[550px] mr-28">
                <p>
                  <span className="font-bold">2. </span>
                  <Trans i18nKey="zoom.instructions.windows.firefox.stepTwo" />
                </p>
              </div>
              <div className="flex">
                <img
                  src={permissionImagePath}
                  alt="Permissions screen"
                  className="w-[228px] h-[75px]"
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px]">
              <p className="w-[80%]">
                <span className="font-bold">3. </span>
                <Trans i18nKey="zoom.instructions.windows.firefox.stepThree" />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.windows.firefox.reloadButton"
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                  token={token}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4 border-2 border-blue-200 p-4 rounded-2xl w-[862px]">
              <p>
                <span className="font-bold">4. </span>
                <Trans
                  i18nKey="zoom.instructions.windows.edge.stepFour"
                  components={{
                    icon: <Menu className=" inline-flex" />,
                    span: <span className="inline-flex items-center gap-1" />,
                    strong: <span className="font-bold" />,
                  }}
                />
              </p>
              <div className="flex gap-2 items-center">
                <img
                  src={settingsImagePath}
                  alt="Settings screen"
                  className="w-[330px] h-[67px]"
                />
              </div>
            </div>
            <div className="flex items-center border-2 border-blue-200 p-4 rounded-2xl w-[862px]">
              <p className="w-[80%]">
                <span className="font-bold">5. </span>
                <Trans i18nKey="zoom.instructions.windows.firefox.stepFive" />
              </p>
              <div className="flex justify-center items-center">
                <ReloadButton
                  brand={brand}
                  translationKey="zoom.instructions.windows.firefox.reloadButton"
                  startDate={startDate}
                  endDate={endDate}
                  language={language}
                  token={token}
                />
              </div>
            </div>
          </>
        )}
      </>
    ),
  }

  const key = `${os}_${browser}`.toLowerCase()

  return (
    browserContentMap[key] || (
      <>
        <p>Navegador no reconocido:</p>
      </>
    )
  )
}

export default DesktopContent
